import { ProfileAvatar, ProfileUsername } from './profile-avatar'
import { ProfileSettingsDropdownMenuItem } from './profile-settings-dropwdown-menu-item'
import { ProfileSettingsTrigger } from './profile-settings-trigger'
import styles from './profile-settings.module.scss'
import { SocialLink } from './social-link'
import { useMediaQuery } from '../../../hook/use-media-query'
import { isFeatureFlagged } from '../../../util/feature-flags'
import { Logout } from '../../profile/logout'
import { FacebookFlatSvgIcon, InstagramSvgIcon, RightArrowSvgIcon, TwitterSvgIcon } from '../../util/images'
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Drawer, DrawerClose, DrawerContent, DrawerTrigger } from 'src/component/ui/drawer'
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger } from 'src/component/ui/dropdown-menu'

interface DrawerMenuItemProps {
  to: string
  children: React.ReactNode
  isActive?: boolean
  className?: string
}

export const ProfileSettings = (): React.ReactElement => {
  const location = useLocation()
  const isMobile = useMediaQuery('(max-width: 992px)')
  const [isOpen, setIsOpen] = useState(false)

  const isActiveRoute = (path: string): boolean => {
    return location.pathname === path
  }

  const handleClose = (): void => {
    setIsOpen(false)
  }

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerTrigger asChild>
          <ProfileSettingsTrigger />
        </DrawerTrigger>
        <DrawerContent className="bg-primary-gray px-10 pb-10 rounded-t-md">
          <ProfileSettingsContent isActiveRoute={isActiveRoute} isDrawer={true} onClose={handleClose} />
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <ProfileSettingsTrigger />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="bottom" sideOffset={8} className="bg-primary-gray p-10 rounded-md w-80">
        <DropdownMenuLabel className="px-0 py-0">
          <ProfileSettingsContent isActiveRoute={isActiveRoute} isDrawer={false} onClose={handleClose} />
        </DropdownMenuLabel>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const DrawerMenuItem = ({ to, children, isActive = false, className = '' }: DrawerMenuItemProps): React.ReactElement => {
  return (
    <div className={className}>
      <DrawerClose asChild>
        <Link
          to={to}
          className={`d-flex justify-between align-center font-evogria text-small hover:text-primary hover:no-underline ${
            styles.link
          } ${isActive ? styles.active : ''}`}
        >
          {children}
          <RightArrowSvgIcon data-testid="right-arrow-icon" />
        </Link>
      </DrawerClose>
    </div>
  )
}

const ProfileSettingsContent = ({
  isActiveRoute,
  isDrawer = false,
  onClose,
}: {
  isActiveRoute: (path: string) => boolean
  isDrawer?: boolean
  onClose?: () => void
}): React.ReactElement => {
  const MenuItemComponent = isDrawer ? DrawerMenuItem : ProfileSettingsDropdownMenuItem

  const handleSocialClick = (): void => {
    if (onClose) {
      onClose()
    }
  }

  return (
    <>
      <div className="flex items-center">
        <ProfileAvatar size="medium" className="mr-2" />
        <ProfileUsername />
      </div>
      <div className="bg-tertiary-grey my-4 h-px" />
      <MenuItemComponent to="/profile/personal" isActive={isActiveRoute('/profile/personal')} className="mb-3">
        Personal info
      </MenuItemComponent>
      <MenuItemComponent to="/profile/overview" isActive={isActiveRoute('/profile/overview')} className="mb-3">
        Overview
      </MenuItemComponent>
      <MenuItemComponent to="/profile/awards" isActive={isActiveRoute('/profile/awards')} className="mb-3">
        Awards
      </MenuItemComponent>
      <MenuItemComponent
        to="/profile/favorites"
        isActive={isActiveRoute('/profile/favorites')}
        className={!isFeatureFlagged('STRAVA') ? 'mb-3' : ''}
      >
        Favorites
      </MenuItemComponent>
      {!isFeatureFlagged('STRAVA') && (
        <MenuItemComponent to="/profile/connections" isActive={isActiveRoute('/profile/connections')}>
          Connections
        </MenuItemComponent>
      )}
      <div className="bg-tertiary-grey my-4 h-px" />
      <Logout />
      <div className="bg-tertiary-grey my-4 h-px" />
      <div className="flex items-center gap-2">
        <SocialLink href="https://www.facebook.com/GRNDHOUSEUK#" onClick={handleSocialClick}>
          <FacebookFlatSvgIcon className="w-5 h-5" data-testid="facebook-icon" />
        </SocialLink>
        <SocialLink href="https://x.com/grndhouseuk" onClick={handleSocialClick}>
          <TwitterSvgIcon className="w-5 h-5" data-testid="twitter-icon" />
        </SocialLink>
        <SocialLink href="https://www.instagram.com/grndhouseapp/" onClick={handleSocialClick}>
          <InstagramSvgIcon className="w-5 h-5" data-testid="instagram-icon" />
        </SocialLink>
      </div>
    </>
  )
}
